/**
 * Browser-side twin of `PrintMaps::Layout` + `PrintMaps::Scale`.
 *
 * Both sides read the same layout.json and run the same Mercator maths, so
 * the rectangle the user drags on screen is byte-for-byte the extent the PDF
 * renders. The previous implementation kept a hand-tuned table of
 * ground-kilometre rectangles here and derived the map area from CSS
 * percentages on the server, so the two disagreed: the printed sheet came out
 * between 6% under and 2% over the requested scale and always covered more
 * ground than the red preview box promised.
 */
import layoutConfig from './layout.json';

export const EARTH_RADIUS_M = 6378137.0;
export const EARTH_CIRCUMFERENCE_M = 2 * Math.PI * EARTH_RADIUS_M;
export const MM_PER_CSS_PIXEL = 25.4 / 96.0;
export const TILE_SIZE_PX = 512.0;
export const MAX_MERCATOR_LATITUDE = 85.051128779806;

export type PageSize = 'a5' | 'a4' | 'a3' | 'letter';
export type Orientation = 'portrait' | 'landscape';

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface LayoutOptions {
  pageSize?: PageSize | string;
  orientation?: Orientation | string;
  showGrid?: boolean;
  showElevationProfile?: boolean;
}

export interface SheetLayout {
  pageSize: PageSize;
  orientation: Orientation;
  pageWidthMm: number;
  pageHeightMm: number;
  marginMm: number;
  headerMm: number;
  footerMm: number;
  gutterMm: number;
  frame: Rect;
  elevation: Rect | null;
}

export interface LngLat {
  lng: number;
  lat: number;
}

export interface Bounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

interface PageSpec {
  widthMm: number;
  heightMm: number;
  marginMm: number;
  headerMm: number;
  footerMm: number;
  gridGutterMm: number;
  elevationProfileMm: number;
  elevationGapMm: number;
}

const PAGES = layoutConfig.pages as unknown as Record<string, PageSpec>;

export const PAGE_SIZES: PageSize[] = ['a5', 'a4', 'a3', 'letter'];
export const SCALE_LADDER: number[] = layoutConfig.scaleLadder as number[];

/**
 * The scales the panel offers as buttons.
 *
 * A shorter list than SCALE_LADDER on purpose: these are the national-series
 * scales a walker recognises (OS Explorer 1:25k and Landranger 1:50k, OSi
 * Discovery 1:50k, IGN/Swisstopo/TK25 1:25k, USGS quads at ~1:25k), where the
 * ladder also carries the in-between rungs "Fit" needs to land on.
 */
export const PICKER_SCALES: number[] = layoutConfig.pickerScales as number[];
export const DEFAULT_SCALE_DENOMINATOR: number = layoutConfig.defaultScaleDenominator as number;
export const RENDER_DPI: number = layoutConfig.renderDpi as number;

export function normalizePageSize(value: unknown): PageSize {
  return PAGE_SIZES.includes(value as PageSize) ? (value as PageSize) : 'a4';
}

export function normalizeOrientation(value: unknown): Orientation {
  return value === 'landscape' ? 'landscape' : 'portrait';
}

export function sheetLayout(options: LayoutOptions = {}): SheetLayout {
  const pageSize = normalizePageSize(options.pageSize);
  const orientation = normalizeOrientation(options.orientation);
  const spec = PAGES[pageSize];
  const landscape = orientation === 'landscape';

  const pageWidthMm = landscape ? spec.heightMm : spec.widthMm;
  const pageHeightMm = landscape ? spec.widthMm : spec.heightMm;
  const gutterMm = options.showGrid ? spec.gridGutterMm : 0;
  const elevationMm = options.showElevationProfile ? spec.elevationProfileMm : 0;
  const elevationGapMm = options.showElevationProfile ? spec.elevationGapMm : 0;

  const contentLeft = spec.marginMm;
  const contentRight = pageWidthMm - spec.marginMm;
  const contentTop = spec.marginMm + spec.headerMm;
  const contentBottom = pageHeightMm - spec.marginMm - spec.footerMm;
  const elevationBlockMm = options.showElevationProfile ? elevationMm + elevationGapMm : 0;

  return {
    pageSize,
    orientation,
    pageWidthMm,
    pageHeightMm,
    marginMm: spec.marginMm,
    headerMm: spec.headerMm,
    footerMm: spec.footerMm,
    gutterMm,
    frame: {
      x: contentLeft + gutterMm,
      y: contentTop + gutterMm,
      width: contentRight - contentLeft - 2 * gutterMm,
      height: contentBottom - contentTop - 2 * gutterMm - elevationBlockMm,
    },
    elevation: options.showElevationProfile
      ? {
          x: contentLeft,
          y: contentBottom - elevationMm,
          width: contentRight - contentLeft,
          height: elevationMm,
        }
      : null,
  };
}

export function clampLatitude(latitude: number): number {
  return Math.min(Math.max(latitude, -MAX_MERCATOR_LATITUDE), MAX_MERCATOR_LATITUDE);
}

const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;
const toDegrees = (radians: number): number => (radians * 180) / Math.PI;

export function groundMetresPerPixel(denominator: number): number {
  return (denominator * MM_PER_CSS_PIXEL) / 1000;
}

/** Exact Mapbox zoom that renders `denominator` at the sheet centre. */
export function zoomForScale(latitude: number, denominator: number): number {
  const lat = clampLatitude(latitude);
  const ratio =
    (EARTH_CIRCUMFERENCE_M * Math.cos(toRadians(lat))) /
    (TILE_SIZE_PX * groundMetresPerPixel(denominator));
  return Math.log2(ratio);
}

/** What paper scale a given camera actually prints at. */
export function scaleForZoom(latitude: number, zoom: number): number {
  const lat = clampLatitude(latitude);
  const mercatorPerPixel = EARTH_CIRCUMFERENCE_M / (TILE_SIZE_PX * Math.pow(2, zoom));
  return (mercatorPerPixel * Math.cos(toRadians(lat))) / (MM_PER_CSS_PIXEL / 1000);
}

export function mercatorY(latitude: number): number {
  const lat = toRadians(clampLatitude(latitude));
  return EARTH_RADIUS_M * Math.log(Math.tan(Math.PI / 4 + lat / 2));
}

export function latitudeFromMercatorY(y: number): number {
  return toDegrees(2 * Math.atan(Math.exp(y / EARTH_RADIUS_M)) - Math.PI / 2);
}

export function mercatorX(longitude: number): number {
  return EARTH_RADIUS_M * toRadians(longitude);
}

export function longitudeFromMercatorX(x: number): number {
  return toDegrees(x / EARTH_RADIUS_M);
}

export function halfExtentsMetres(
  centerLat: number,
  denominator: number,
  frameWidthMm: number,
  frameHeightMm: number,
): [number, number] {
  const inflation = 1 / Math.cos(toRadians(clampLatitude(centerLat)));
  return [
    ((frameWidthMm / 1000) * denominator * inflation) / 2,
    ((frameHeightMm / 1000) * denominator * inflation) / 2,
  ];
}

/** Screen offsets (x right, y down) rotated into Mercator (east, north). */
export function rotateOffset(dx: number, dy: number, bearingDegrees: number): [number, number] {
  const bearing = toRadians(bearingDegrees);
  const cos = Math.cos(bearing);
  const sin = Math.sin(bearing);
  return [dx * cos - dy * sin, -dx * sin - dy * cos];
}

export interface SheetGeometryOptions {
  center: LngLat;
  denominator: number;
  frameWidthMm: number;
  frameHeightMm: number;
  bearingDegrees?: number;
}

/** The four printed corners, in [nw, ne, se, sw] order. */
export function sheetCorners({
  center,
  denominator,
  frameWidthMm,
  frameHeightMm,
  bearingDegrees = 0,
}: SheetGeometryOptions): Array<[number, number]> {
  const [halfW, halfH] = halfExtentsMetres(center.lat, denominator, frameWidthMm, frameHeightMm);
  const cx = mercatorX(center.lng);
  const cy = mercatorY(center.lat);

  return (
    [
      [-halfW, -halfH],
      [halfW, -halfH],
      [halfW, halfH],
      [-halfW, halfH],
    ] as Array<[number, number]>
  ).map(([dx, dy]) => {
    const [east, north] = rotateOffset(dx, dy, bearingDegrees);
    return [longitudeFromMercatorX(cx + east), latitudeFromMercatorY(cy + north)] as [number, number];
  });
}

/** Axis-aligned lon/lat envelope of the printed sheet. */
export function sheetBounds({
  center,
  denominator,
  frameWidthMm,
  frameHeightMm,
  bearingDegrees = 0,
}: SheetGeometryOptions): Bounds {
  const [halfW, halfH] = halfExtentsMetres(center.lat, denominator, frameWidthMm, frameHeightMm);
  const bearing = toRadians(bearingDegrees);
  const cos = Math.abs(Math.cos(bearing));
  const sin = Math.abs(Math.sin(bearing));
  const extentX = halfW * cos + halfH * sin;
  const extentY = halfW * sin + halfH * cos;
  const cx = mercatorX(center.lng);
  const cy = mercatorY(center.lat);

  return {
    west: longitudeFromMercatorX(cx - extentX),
    east: longitudeFromMercatorX(cx + extentX),
    south: latitudeFromMercatorY(cy - extentY),
    north: latitudeFromMercatorY(cy + extentY),
  };
}

/** Closed GeoJSON ring of the printed sheet, for the preview overlay. */
export function sheetPolygon(options: SheetGeometryOptions): GeoJSON.Feature<GeoJSON.Polygon> {
  const corners = sheetCorners(options);
  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [[...corners, corners[0]]],
    },
  };
}

export function boundsToMultiPolygonWkt(bounds: Bounds): string {
  const w = bounds.west.toFixed(10);
  const e = bounds.east.toFixed(10);
  const s = bounds.south.toFixed(10);
  const n = bounds.north.toFixed(10);
  return `MULTIPOLYGON (((${w} ${s}, ${e} ${s}, ${e} ${n}, ${w} ${n}, ${w} ${s})))`;
}

/**
 * Smallest ladder scale whose sheet swallows the whole bounding box, or null
 * when even the coarsest entry is too small (the caller must tile instead of
 * silently cropping the route).
 */
export function fitDenominator(
  bbox: Bounds,
  frameWidthMm: number,
  frameHeightMm: number,
  paddingFraction = 0.06,
  ladder: number[] = SCALE_LADDER,
): number | null {
  const centerLat = (bbox.north + bbox.south) / 2;
  const neededX = Math.abs(mercatorX(bbox.east) - mercatorX(bbox.west));
  const neededY = Math.abs(mercatorY(bbox.north) - mercatorY(bbox.south));
  const pad = 1 + paddingFraction * 2;

  const found = ladder.find((denominator) => {
    const [halfW, halfH] = halfExtentsMetres(centerLat, denominator, frameWidthMm, frameHeightMm);
    return halfW * 2 >= neededX * pad && halfH * 2 >= neededY * pad;
  });

  return found ?? null;
}

/** "1:25,000" for display. */
export function formatScale(denominator: number): string {
  return `1:${denominator.toLocaleString('en-US')}`;
}

/** How much ground the sheet covers, for the "covers 5.0 × 6.5 km" hint. */
export function sheetCoverageKm(
  denominator: number,
  frameWidthMm: number,
  frameHeightMm: number,
): { widthKm: number; heightKm: number } {
  return {
    widthKm: (frameWidthMm / 1000) * denominator / 1000,
    heightKm: (frameHeightMm / 1000) * denominator / 1000,
  };
}
